import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  storeAddressSchema,
  type OfflineStoreData,
  type StoreAddressFormValues,
  type StoreData,
} from '~domains/partner-application/entities/application';
import { SelectMall } from '~domains/partner-application/features/select-mall';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_STORES,
} from '~domains/partner-application/shared/constants';

import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';
import { AddressSuggestions } from '@breezeventures/web-ph-address-suggestions/address-suggestions';
import { zodResolver } from '@hookform/resolvers/zod';

const suggestionFieldNames = {
  country: 'country',
  region: 'region',
  province: 'province',
  cityOrMunicipality: 'cityOrMunicipality',
  barangay: 'barangay',
  postalCode: 'postalCode',
};

type StoreAddressProps = {
  storeDraft?: Partial<StoreData> | null;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (data: StoreAddressFormValues) => Promise<void>;
};

export const StoreAddress = ({
  storeDraft,
  onBack,
  onClose,
  onSubmit,
}: StoreAddressProps) => {
  const offlineStoreDraft = storeDraft as OfflineStoreData | null;

  const methods = useForm<StoreAddressFormValues>({
    resolver: zodResolver(storeAddressSchema),
    mode: 'all',
    defaultValues: offlineStoreDraft?.address ?? {
      country: 'PHL',
      region: '',
      province: '',
      cityOrMunicipality: '',
      postalCode: '',
      barangay: '',
      streetAndHouse: '',
      unitAndFloor: '',
      mallId: '',
      mallNameOther: '',
    },
  });

  useEffect(() => {
    if (offlineStoreDraft?.address) {
      methods.trigger();
    }
  }, [offlineStoreDraft?.address, methods]);

  const selectedMall = methods.watch('mallId');
  const showOtherMallInput = selectedMall === 'OTHER';

  return (
    <form
      onSubmit={methods.handleSubmit(onSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<ScreenHeader onBack={onBack} onClose={onClose} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={STEP_INDEX_STORES}
            totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <FormProvider {...methods}>
          <Typography.Title size="s" className="py-6">
            Enter your store&apos;s complete address
          </Typography.Title>

          <div className="flex flex-col gap-3">
            <AddressSuggestions fieldNames={suggestionFieldNames} />

            <Controller
              name="streetAndHouse"
              control={methods.control}
              render={(props) => (
                <TextField
                  {...props}
                  label="House number, Street name"
                  labelPosition="inner"
                />
              )}
            />

            <SelectMall name="mallId" />

            {showOtherMallInput && (
              <Controller
                shouldUnregister
                name="mallNameOther"
                control={methods.control}
                render={(props) => (
                  <TextField
                    {...props}
                    label="Mall name (Helps customers find you)"
                    labelPosition="inner"
                  />
                )}
              />
            )}
          </div>
        </FormProvider>
      </Screen>
    </form>
  );
};
